import { background, blue, button, divider, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { TOption } from "@/types/common";
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Box,
  Chip,
  CircularProgress,
  FilterOptionsState,
  FormHelperText,
  MenuItem,
  TextField,
  css,
  styled,
} from "@mui/material";
import { HTMLAttributes, Key, SyntheticEvent, forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { useNearBottomFetch } from "@/hooks/useNearBottomFetch";
import { useSearchList, AsyncSearchConfig } from "@/hooks/useSearchList";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";

export type TComboboxMultiAsyncProps<T> = {
  /** Data source (query + fetcher + option mapper). The dropdown owns lazy-load/search/filter internally. */
  searchConfig: AsyncSearchConfig<T>;
  /** Pass false to hold off fetching (e.g. while a caller is about to seed this dropdown's cache itself). Default true. */
  enabled?: boolean;
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  limitTags?: number;
};

type TAsyncListboxProps = HTMLAttributes<HTMLUListElement> & { loadingMore?: boolean };

// Custom listbox that appends a loading spinner row at the bottom of the popup
const AsyncListbox = forwardRef<HTMLUListElement, TAsyncListboxProps>(function AsyncListbox({ children, loadingMore, ...rest }, ref) {
  return (
    <ul {...rest} ref={ref}>
      {children}
      {loadingMore && (
        <LoadingRow>
          <CircularProgress color="inherit" size={18} />
        </LoadingRow>
      )}
    </ul>
  );
});

// Render-independent Autocomplete config, hoisted out of JSX (no logic in render).
const isPartnerOptionEqual = (option: unknown, val: unknown) => (option as TOption).value === (val as TOption).value;
const getPartnerOptionLabel = (option: unknown) => (option as TOption).label;

// FE-only mode (full list loaded): narrow client-side by the typed text, like ComboboxMulti.
const filterPartnerOptions = (opts: unknown[], state: FilterOptionsState<unknown>): unknown[] => {
  if (state.inputValue === "") return opts;
  const query = convertFullWidthToHalfWidth(state.inputValue);
  return opts.filter((opt) => {
    const label = convertFullWidthToHalfWidth((opt as TOption).label);
    return label.includes(query) || label.toLowerCase().includes(query.toLowerCase());
  });
};

// BE-search mode: options already server-filtered → pass through (MUI re-filter would widen them back).
const passThroughFilter = (opts: unknown[]): unknown[] => opts;

const renderPartnerOption = (props: HTMLAttributes<HTMLLIElement> & { key?: Key }, option: unknown) => {
  // Key by unique `value` (id), not MUI's label-derived key — duplicate names would collide.
  const { key: _key, ...rest } = props;
  const opt = option as TOption;
  return (
    <MenuItem {...rest} key={opt.value} value={opt.value} title={opt.label}>
      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.label}</div>
    </MenuItem>
  );
};

const renderPartnerTags = (tagValues: unknown[], getTagProps: AutocompleteRenderGetTagProps) =>
  (tagValues as TOption[]).map((option, index) => {
    const { key, ...tagProps } = getTagProps({ index });
    return <Chip key={key} label={option.label} size="small" disabled={tagProps.disabled} onDelete={tagProps.onDelete} style={{ maxWidth: 120 }} />;
  });

const AUTOCOMPLETE_COMPONENTS_PROPS = {
  paper: { sx: { fontSize: "14px !important", ".MuiMenuItem-root": typography.body14 } },
};

export function ComboboxMultiAsync<T>({
  searchConfig,
  enabled = true,
  value = [],
  onChange,
  placeholder = "選択してください",
  helperText,
  disabled,
  limitTags = 2,
}: TComboboxMultiAsyncProps<T>) {
  // Lazy-load / search / FE-vs-BE switching all live in the hook; this component is pure presentation.
  const { options, allLoaded, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, onInputChange } = useSearchList(
    searchConfig,
    enabled,
  );

  // Controlled input text: prevents MUI from blanking the typed keyword when options reload after a fetch.
  const [inputValue, setInputValue] = useState("");

  // Cache of every loaded option so a selected chip keeps its label across search/page changes.
  // Updated inside this memo (not a separate one) to avoid cross-memo evaluation-order dependence.
  const optionCacheRef = useRef<Map<string, TOption>>(new Map());
  const selectedOptions = useMemo(() => {
    options.forEach((opt) => optionCacheRef.current.set(String(opt.value), opt));
    return value.map((v) => optionCacheRef.current.get(v) ?? { value: v, label: v });
  }, [value, options]);

  // All loaded options + any selected not in the current page (to preserve chips)
  const displayOptions = useMemo(() => {
    const selectedNotInPage = selectedOptions.filter((sel) => !options.find((opt) => opt.value === sel.value));
    return [...options, ...selectedNotInPage];
  }, [options, selectedOptions]);

  const handleChange = (_e: SyntheticEvent<unknown>, newValue: unknown) => {
    const newValues = (newValue as TOption[]).map((opt) => String(opt.value));
    onChange?.(newValues);
    // Match ComboboxMulti: clear the search box after select/remove → list returns to unfiltered
    setInputValue("");
    onInputChange?.("");
  };

  const handleListboxScroll = useNearBottomFetch({ hasNextPage, isFetchingNextPage, fetchNextPage });

  // Loader stays at the bottom while more pages exist (hasNextPage ⟹ full page loaded ⟹ scrollable).
  const showLoadingRow = Boolean(hasNextPage) || isFetchingNextPage || (isFetching && options.length === 0);

  // Ignore MUI's 'reset' (val="" on options reload/select) so the typed keyword persists.
  // 'input' → type; 'clear' (X) → reset to the unfiltered list.
  const handleInputChange = useCallback(
    (_e: SyntheticEvent, val: string, reason: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setInputValue(val);
        onInputChange?.(val);
      } else if (reason === "clear") {
        setInputValue("");
        onInputChange?.("");
      }
    },
    [onInputChange],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => <TextField {...params} placeholder={selectedOptions.length === 0 ? placeholder : ""} />,
    [selectedOptions.length, placeholder],
  );

  const listboxProps = useMemo(
    () =>
      ({ onScroll: handleListboxScroll as React.UIEventHandler<HTMLUListElement>, loadingMore: showLoadingRow }) as HTMLAttributes<HTMLUListElement>,
    [handleListboxScroll, showLoadingRow],
  );

  return (
    <BoxWrapper position="relative">
      <StyledAutocomplete
        multiple
        limitTags={limitTags}
        noOptionsText="選択肢なし"
        // While the result is in-flight and nothing loaded yet, show a spinner instead of "選択肢なし".
        loading={isFetching && options.length === 0}
        loadingText={
          <LoadingRow as="div">
            <CircularProgress color="inherit" size={18} />
          </LoadingRow>
        }
        disabled={disabled}
        value={selectedOptions}
        inputValue={inputValue}
        onChange={handleChange}
        onInputChange={handleInputChange}
        filterOptions={allLoaded ? filterPartnerOptions : passThroughFilter}
        isOptionEqualToValue={isPartnerOptionEqual}
        getOptionLabel={getPartnerOptionLabel}
        renderInput={renderInput}
        options={displayOptions}
        ListboxComponent={AsyncListbox}
        ListboxProps={listboxProps}
        renderOption={renderPartnerOption}
        renderTags={renderPartnerTags}
        componentsProps={AUTOCOMPLETE_COMPONENTS_PROPS}
      />
      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}
    </BoxWrapper>
  );
}

const BoxWrapper = styled(Box)``;

const LoadingRow = styled("li")`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  color: ${text.secondary};
  list-style: none;
`;

const StyledAutocomplete = styled(Autocomplete)`
  width: 100%;
  .MuiAutocomplete-inputRoot {
    padding: 2px 56px 2px 4px !important;
    min-height: 34px;
    flex-wrap: nowrap;
    overflow: hidden;
    gap: 2px;
  }
  .MuiAutocomplete-inputRoot.Mui-focused {
    flex-wrap: wrap;
    overflow: visible;
  }

  .MuiAutocomplete-tag[class*="limitTag"] {
    font-size: 12px;
    max-width: none;
  }

  .MuiInputBase-root {
    position: relative;
    background: ${background.white};
    .MuiSelect-select {
      color: ${text.primary};
    }

    input::placeholder {
      color: ${text.primary};
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-focused {
      &::after {
        content: "";
        position: absolute;
        top: -4px;
        left: -4px;
        width: 100%;
        height: 100%;
        padding: 2px;
        border: 2px solid ${button.secondary};
        box-sizing: content-box;
        border-radius: ${rounded.sm};
        z-index: -1;
      }
    }

    .MuiInputBase-input {
      padding: 4px 8px;
      ${css(typography.body14)};
      min-width: 30px !important;
    }

    &.Mui-disabled {
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
  }
`;
